import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateWikiPage,
  MatterWikiResponse,
  UpdateWikiPage,
  WikiPage,
  WikiPageListItem,
} from '../types/matter-wiki-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const matterWikiApi = createApi({
  reducerPath: 'matterWikiApi',
  tagTypes: ['Wiki', 'WikiPages', 'WikiPage'],
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    // Get wiki with page tree for a matter
    getWiki: builder.query<MatterWikiResponse, { matterId: string }>({
      query: ({ matterId }) => `api/matters/${matterId}/wiki/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'Wiki', id: matterId },
      ],
    }),

    // Trigger wiki generation
    generateWiki: builder.mutation<
      MatterWikiResponse,
      { matterId: string; force?: boolean }
    >({
      query: ({ matterId, force }) => ({
        url: `api/matters/${matterId}/wiki/generate/${force ? '?force=true' : ''}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Wiki', id: matterId },
      ],
    }),

    // List all wiki pages for a matter
    getWikiPages: builder.query<WikiPageListItem[], { matterId: string }>({
      query: ({ matterId }) => `api/matters/${matterId}/wiki/pages/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'WikiPages', id: matterId },
        ...(result || []).map(({ id }) => ({
          type: 'WikiPage' as const,
          id,
        })),
      ],
    }),

    // Get a single wiki page by ID
    getWikiPage: builder.query<
      WikiPage,
      { matterId: string; pageId: string }
    >({
      query: ({ matterId, pageId }) =>
        `api/matters/${matterId}/wiki/pages/${pageId}/`,
      providesTags: (result, error, { pageId }) => [
        { type: 'WikiPage', id: pageId },
      ],
    }),

    // Get a wiki page by path
    getWikiPageByPath: builder.query<
      WikiPage,
      { matterId: string; path: string }
    >({
      query: ({ matterId, path }) =>
        `api/matters/${matterId}/wiki/pages/?path=${encodeURIComponent(path)}`,
      providesTags: (result) =>
        result ? [{ type: 'WikiPage', id: result.id }] : [],
    }),

    // Create a new wiki page
    createWikiPage: builder.mutation<
      WikiPage,
      { matterId: string; page: CreateWikiPage }
    >({
      query: ({ matterId, page }) => ({
        url: `api/matters/${matterId}/wiki/pages/`,
        method: 'POST',
        body: page,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'WikiPages', id: matterId },
        { type: 'Wiki', id: matterId },
      ],
    }),

    // Update a wiki page
    updateWikiPage: builder.mutation<
      WikiPage,
      { matterId: string; pageId: string; page: UpdateWikiPage }
    >({
      query: ({ matterId, pageId, page }) => ({
        url: `api/matters/${matterId}/wiki/pages/${pageId}/`,
        method: 'PATCH',
        body: page,
      }),
      invalidatesTags: (result, error, { matterId, pageId }) => [
        { type: 'WikiPages', id: matterId },
        { type: 'WikiPage', id: pageId },
        { type: 'Wiki', id: matterId },
      ],
    }),

    // Delete a wiki page
    deleteWikiPage: builder.mutation<
      void,
      { matterId: string; pageId: string }
    >({
      query: ({ matterId, pageId }) => ({
        url: `api/matters/${matterId}/wiki/pages/${pageId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId, pageId }) => [
        { type: 'WikiPages', id: matterId },
        { type: 'WikiPage', id: pageId },
        { type: 'Wiki', id: matterId },
      ],
    }),
  }),
});

export const {
  useGetWikiQuery,
  useGenerateWikiMutation,
  useGetWikiPagesQuery,
  useGetWikiPageQuery,
  useGetWikiPageByPathQuery,
  useCreateWikiPageMutation,
  useUpdateWikiPageMutation,
  useDeleteWikiPageMutation,
} = matterWikiApi;
